import { Component, OnInit, OnDestroy } from '@angular/core';
import { ProcessService } from '../../services/process.service';
import { Process, ProcessStatus } from 'src/app/core/models/process.model';
import { MatDialog } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { ProcessTableComponent } from 'src/app/shared/components/process-table/process-table.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router, RouterModule } from '@angular/router';
import { ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatTabsModule } from '@angular/material/tabs';
import { MatInputModule } from '@angular/material/input';
import { ProcessDialogComponent } from '../../components/dialogs/process-creation/process-dialog/process-dialog.component';
import { ProcessTypeDialogComponent } from '../../components/dialogs/process-creation/process-type-dialog/process-type-dialog.component';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { BehaviorSubject, firstValueFrom, forkJoin, Subject, throwError } from 'rxjs';
import { AuthService } from 'src/app/core/services/AuthService/auth.service';
import { catchError, finalize, map, takeUntil } from 'rxjs/operators';
import { SyncService } from 'src/app/core/services/SyncService/syncService.service';
import { ProcessFilterDialogComponent } from '../../components/shared/process-filter/process-filter-dialog.component';
import { JSONSchema } from '@ngx-pwa/local-storage';
import { RequirementFilterDialogComponent } from '../../components/shared/process-filter-requirement/requirement-filter-dialog.component';
import { URLBuilder } from 'src/app/core/utils/url-builder';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';


@Component({
  selector: 'app-process-page',
  standalone: true,
  imports: [
    CommonModule,
    ProcessTableComponent,
    MatCheckboxModule,
    MatIconModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    RouterModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatTabsModule,
    MatInputModule,
    MatSnackBarModule,
    ProcessFilterDialogComponent,
    RequirementFilterDialogComponent
  ],
  templateUrl: './process-page.component.html',
})
export class ProcessPageComponent implements OnInit, OnDestroy {
  processes: Process[] = []; // Todos os processos
  filteredProcesses: Process[] = []; // Processos filtrados
  loading$ = new BehaviorSubject<boolean>(true);

  private urlBuilder = new URLBuilder();
  private readonly destroy$ = new Subject<void>();
  private readonly schema: JSONSchema = {
    type: 'object',
    properties: {
      id: { type: 'number' },
      descricao: { type: 'string' },
    },
    required: ['id', 'descricao'],
  };

  private isAuthenticating = false; 

  constructor(
    private readonly processService: ProcessService,
    private readonly authService: AuthService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly syncService: SyncService,
    private readonly http: HttpClient,
    private readonly router: Router
  ) { }

  ngOnInit(): void {
    // Retrieve email and password from router state
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras?.state as { email: string; password: string };
    
    if (state?.email && state?.password) {
      // Call checkAuthentication with dynamic credentials
      this.checkAuthentication(state.email, state.password);
    } else {
      this.showSnackbar('Erro: Credenciais de login não fornecidas.');
    }
  
    window.addEventListener('online', () => this.syncOfflineProcesses());
  }  


  private checkAuthentication(email: string, password: string): void {
    if (!this.authService.isAuthenticated() && !this.isAuthenticating) {
      this.isAuthenticating = true; // Prevent further calls
      this.authenticateAndLoadProcesses(email, password);
    } else {
      this.consultProcesses();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    window.removeEventListener('online', () => this.syncOfflineProcesses()); // Clean up listener
  }

  // Autenticar e carregar processos
  private authenticateAndLoadProcesses(email: string, password: string): void {
    this.authService.loginLocal(email, password)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (success) => {
          this.isAuthenticating = false; // Reset the flag after authentication attempt
          if (success) {
            this.consultProcesses();
          } else {
            this.authService.notifyUser('Falha na autenticação');
          }
        },
        error: () => {
          this.isAuthenticating = false;
          this.authService.notifyUser('Erro ao fazer login');
        }
      });
  }
  // Ajuste o método loadProcesses em ProcessPageComponent
  private loadProcesses(): void {
    this.loading$.next(true);

    this.processService.getProcessesOffline(this.schema)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: Process[]) => {

          this.processes = data;
          this.filteredProcesses = [...this.processes];
          this.loading$.next(false);
        },
        error: () => {
          this.loading$.next(false);
          this.showSnackbar('Erro ao carregar processos.');
        },
      });
  }

  async consultProcesses(): Promise<void> {
    const token = await this.getToken();
    console.log("token",token);
    if (!token) {
      this.showSnackbar('Falha na autenticação. Não foi possível obter o token.');
      return;
    }
    console.log("Token obtido com sucesso:", token); // Log the obtained token
    this.loading$.next(true);
  
    const idsTipoProcesso = [104, 152];
    const observables = idsTipoProcesso.map(idTipoProcesso =>
      this.processService.listProcesses(
        idTipoProcesso,
        token,
        0,     // offset
        10,    // limit
        true,  // meusprocessos
        true   // exibiretapas
      ).pipe(
        map((response: any) => {
          // Check if 'listaObj' exists and is an array
          if (!response || !Array.isArray(response.listaObj)) {
            console.warn('Estrutura inesperada de resposta:', response);
            return []; // Return an empty array if structure is unexpected
          }
          // Map 'listaObj' to the expected format
          return response.listaObj.map((item: any) => ({
            id: item.id || 0,
            idIntegracao: item.idIntegracao,
            codigo: item.codigo,
            fase: item.fase || '',
            descricao: item.descricao,
            numeroProcesso: item.numeroProcesso,
            idTipoProcesso: item.idTipoProcesso,
            nomeTipoProcesso: item.nomeTipoProcesso || '',
            idModalidade: item.idModalidade,
            idModalidadeIntegracao: item.idModalidadeIntegracao,
            idModalidadeGrupo: item.idModalidadeGrupo,
            idModalidadeGrupoIntegracao: item.idModalidadeGrupoIntegracao,
            empresa: {
              id: item.empresa?.id || 0,
              idIntegracao: item.empresa?.idIntegracao || 0,
              codigo: item.empresa?.codigo || '',
              cpfCnpj: item.empresa?.cpfCnpj || '',
              nome: item.empresa?.nome || '',
              email: item.empresa?.email,
              inscricaoMunicipal: item.empresa?.inscricaoMunicipal || '',
              endereco: {
                id: item.empresa?.endereco?.id || 0,
                cep: item.empresa?.endereco?.cep || '',
                logradouro: item.empresa?.endereco?.logradouro || '',
                numero: item.empresa?.endereco?.numero || '',
                complemento: item.empresa?.endereco?.complemento,
                bairro: item.empresa?.endereco?.bairro || '',
                cidade: item.empresa?.endereco?.cidade || '',
                referencia: item.empresa?.endereco?.referencia,
                uf: item.empresa?.endereco?.uf || '',
                principal: item.empresa?.endereco?.principal || false,
                tipoEndereco: item.empresa?.endereco?.tipoEndereco || 'COMERCIAL'
              },
              enderecos: item.empresa?.enderecos || [],
              telefone: item.empresa?.telefone,
              situacao: item.empresa?.situacao
            },
            imovel: item.imovel || null,
            dataDeInicio: item.dataDeInicio,
            situacao: item.situacao || ProcessStatus.Ativo,
            etapas: item.etapas || [],
            dueDays: item.dueDays || 0,
            registerDate: item.registerDate
          }) as Process);
        })
      )
    );
  
    // Combine results from both observables
    forkJoin(observables)
      .pipe(finalize(() => this.loading$.next(false)))
      .subscribe({
        next: (results: Process[][]) => {
          // Flatten the array to get a single list of processes
          this.processes = results.flat();
          this.filteredProcesses = [...this.processes]; // Updated line to use all processes
  
          // Log to verify loaded data
          console.log("Processos carregados para a tabela:", this.processes);
          console.log("Processos filtrados:", this.filteredProcesses);
  
          // Pass the data to the process table (if needed by setting an Input)
          this.showSnackbar(`${this.processes.length} processos encontrados`);
        },
        error: (error) => {
          console.error('Erro ao carregar processos:', error);
          this.showSnackbar('Erro ao carregar processos');
        }
      });
  }


  private async getToken(): Promise<string | null> {
    const token = await this.authenticate();
    if (token) {
      localStorage.setItem('authToken', token);
      return token;
    }
    return null;  
  }

  private async authenticate(): Promise<string | null> {
    const loginData = { login: '33870041000198', senha: 'HgtUop@8' };
    const url = this.urlBuilder.buildEndpoint('service/autenticacao/login');

    try {
      const response = await firstValueFrom(
        this.http.post<{ listaObj: { token: string }[] }>(url, loginData, {
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
        })
      );
      return response?.listaObj?.[0]?.token || null;
    } catch (error) {
      this.handleAuthError(error as HttpErrorResponse);
      return null;
    }
  }

  private handleAuthError(error: HttpErrorResponse): void {
    if (error.status === 400) {
      this.showSnackbar('Requisição inválida. Verifique os dados de login.');
    } else if (error.status === 401) {
      this.showSnackbar('Não autorizado. Verifique suas credenciais.');
    } else if (error.status === 500) {
      this.showSnackbar('Erro interno do servidor. Tente novamente mais tarde.');
    } else {
      this.showSnackbar('Erro desconhecido ao autenticar.');
    }
  }

  // Abrir diálogo de filtro
  openProcessFilterDialog(): void {
    const dialogRef = this.dialog.open(ProcessFilterDialogComponent, {
      width: '500px',
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((filters) => {
        if (filters) this.applyFilters(filters);
      });
  }

  // Abrir diálogo de filtro
  openRequirementFilterDialog(): void {
    const dialogRef = this.dialog.open(RequirementFilterDialogComponent, {
      width: '500px',
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((filters) => {
        if (filters) this.applyFilters(filters);
      });
  }

  // Aplicar filtros na lista de processos
  applyFilters(filters: any): void {
    this.filteredProcesses = this.processes.filter((process) => {
      return Object.keys(filters).every((key) => {
        const typedKey = key as keyof Process;
        const value = filters[key]?.toLowerCase();
        const processValue = process[typedKey]?.toString().toLowerCase();
        return value ? processValue?.includes(value) : true;
      });
    });
  }

  // Abrir diálogo para criação de novo processo
  openCreateProcessDialog(): void {
    const dialogRef = this.dialog.open(ProcessTypeDialogComponent, {
      width: '400px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.openProcessDetailsDialog(result);
      }
    });
  }

  // Abrir detalhes do processo
  openProcessDetailsDialog(data: { processTypeName: string; dueDays: number }): void {
    const dialogRef = this.dialog.open(ProcessDialogComponent, {
      width: '600px',
      data,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.saveProcess(data, result);
      }
    });
  }

  // Salvar ou criar novo processo
  private saveProcess(processTypeData: any, processData: any): void {
    const newProcess = this.processService.buildProcess(processTypeData, processData);
    console.log('Saving process with data:', newProcess);

    this.processService.saveOrCreateProcess(newProcess, this.schema)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (createdProcess) => {
          if (createdProcess) {
            console.log('Processo criado/salvo com sucesso:', createdProcess);

            // Ensure the process is added to both arrays
            this.processes.push(createdProcess);
            this.filteredProcesses = [...this.processes];

            // Force refresh if necessary
            this.loadProcesses();
            this.showSnackbar('Processo criado/salvo com sucesso!');
          }
        },
        error: (error) => {
          console.error('Erro ao salvar/criar o processo:', error);
          this.showSnackbar('Erro ao salvar/criar o processo.');
        }
      });
  }

  // Sincronizar processos offline
  public syncOfflineProcesses(): void {
    this.syncService.syncOfflineProcesses()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.showSnackbar('Processos sincronizados com sucesso!'),
        error: () => this.showSnackbar('Erro ao sincronizar processos offline.'),
      });
  }

  // Mostrar mensagem no snackbar
  private showSnackbar(message: string, duration = 3000): void {
    this.snackBar.open(message, 'Fechar', { duration });
  }
}